/** Drag a bottom sheet down to dismiss it.
 *
 * The sheet grew a grabber pill because that is what tells a thumb "this
 * slides" before anyone tries it. The pill was decoration: it made a promise
 * the sheet did not keep, which is worse than having no pill, because the
 * person tries it, nothing moves, and they learn the surface is inert.
 *
 * So the promise is kept. Dragging from the header follows the finger, past a
 * threshold it dismisses, and short of it it springs back. The drag starts from
 * the HEADER only — the body scrolls, and a sheet that fights its own scroll
 * view for the same gesture is the other way to make a surface feel broken.
 *
 * Direct DOM writes, not React state: a re-render per pointer-move would drop
 * frames on the device this exists for.
 */
import * as React from "react";

/** How far down the sheet must be dragged to dismiss, in px. */
const DISMISS_PX = 110;
/** …or how fast it must be flung, in px per ms, however far it got. */
const FLING_VELOCITY = 0.55;
/** Past this the sheet resists rather than tracking 1:1, so the gesture always
 *  feels attached to something. */
const RESIST_ABOVE = 240;

export interface SheetDrag {
  /** Spread onto the element that may START a drag (the header). */
  handleProps: {
    onPointerDown: (e: React.PointerEvent) => void;
    style: React.CSSProperties;
  };
  /** Attach to the sheet itself — the thing that moves. A callback ref, so it
   *  satisfies React 18's `Ref<T>` without the null-vs-non-null argument that a
   *  RefObject starts. */
  sheetRef: (el: HTMLDivElement | null) => void;
}

export function useSheetDrag(enabled: boolean, onDismiss: () => void): SheetDrag {
  const sheetRef = React.useRef<HTMLDivElement | null>(null);
  const drag = React.useRef<{ id: number; y0: number; t0: number; dy: number } | null>(null);

  const paint = React.useCallback((dy: number, animate: boolean) => {
    const el = sheetRef.current;
    if (!el) return;
    el.style.transition = animate ? "transform 260ms cubic-bezier(0.32, 0.72, 0, 1)" : "none";
    el.style.transform = dy > 0 ? `translateY(${dy}px)` : "";
  }, []);

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || drag.current) return;
      // Primary contact only — never a right click or a stylus barrel.
      if (e.button !== 0) return;
      drag.current = { id: e.pointerId, y0: e.clientY, t0: performance.now(), dy: 0 };
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    [enabled],
  );

  React.useEffect(() => {
    if (!enabled) return;
    const move = (e: PointerEvent) => {
      const d = drag.current;
      if (!d || e.pointerId !== d.id) return;
      const raw = e.clientY - d.y0;
      // Upward drags do nothing: there is nowhere further up to go, and a sheet
      // that lifts off its anchor looks like a bug rather than a detent.
      if (raw <= 0) {
        d.dy = 0;
        paint(0, false);
        return;
      }
      d.dy = raw <= RESIST_ABOVE ? raw : RESIST_ABOVE + (raw - RESIST_ABOVE) * 0.25;
      paint(d.dy, false);
    };
    const end = (e: PointerEvent) => {
      const d = drag.current;
      if (!d || e.pointerId !== d.id) return;
      drag.current = null;
      const ms = Math.max(1, performance.now() - d.t0);
      const flung = d.dy / ms > FLING_VELOCITY;
      if (d.dy > DISMISS_PX || (flung && d.dy > 24)) {
        // Let it finish leaving before it unmounts, so the sheet exits in the
        // direction the hand threw it rather than blinking out.
        paint(window.innerHeight, true);
        window.setTimeout(onDismiss, 180);
        return;
      }
      paint(0, true);
    };
    window.addEventListener("pointermove", move, { passive: true });
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [enabled, onDismiss, paint]);

  // A fresh open must never inherit the last drag's transform.
  React.useEffect(() => {
    if (enabled) paint(0, false);
  }, [enabled, paint]);

  const setSheet = React.useCallback((el: HTMLDivElement | null) => {
    sheetRef.current = el;
  }, []);

  return {
    sheetRef: setSheet,
    handleProps: {
      onPointerDown,
      // The header owns this gesture; the browser must not also try to scroll
      // or text-select with it.
      style: enabled ? { touchAction: "none" } : {},
    },
  };
}
